//predataflow.js
import React, { useState, useRef, useEffect } from "react";
import { ImageIcon, CheckCircle, Camera, Upload, Sparkles, Settings, Eye, RefreshCw, Play, Grid3x3, ChevronLeft, ChevronRight, Trash2, LayoutDashboard } from "lucide-react";
import Header from '../header/header2';
import { CloseOutlined } from "@ant-design/icons";
import { Modal, Select, InputNumber, Button, message, Badge, Tabs, Tag } from "antd";
import axios from "../../utils/axios";
import {
  DragDropContext,
  Droppable,
  Draggable
} from "@hello-pangea/dnd";
import { useNavigate, useLocation } from 'react-router-dom';

const { TabPane } = Tabs;

const PipelineCard = ({ label, image, images = [], locked, onPreview, onDelete }) => (
  <div
    className={`min-w-[220px] max-w-[220px] bg-gray-50 rounded-xl overflow-hidden border-2
      ${locked ? "border-gray-300 opacity-95" : "border-violet-400 cursor-move"}
    `}
  >
    <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase">{label}</span>

        {!locked && onDelete && (
          <button
            onClick={onDelete}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 hover:bg-red-500 transition"
          >
            <CloseOutlined style={{ fontSize: 10 }} />
          </button>
        )}
      </div>
    </div>

    <div className="relative h-44 flex items-center justify-center bg-white group">

      {/* MULTI IMAGE GRID */}
      {images.length > 1 ? (
        <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
          {images.slice(0,4).map((img, i) => (
            <img
              key={i}
              src={img}
              className="w-full h-full object-contain border"
              onClick={() => onPreview?.(img)}
            />
          ))}
        </div>
      ) : image ? (
        <>
          <img
            src={image}
            className="max-h-full max-w-full object-contain"
            alt={label}
          />

          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
            <button
              onClick={() => onPreview?.(image)}
              className="bg-white rounded-full p-2 shadow-lg"
            >
              <Eye className="w-5 h-5 text-gray-800" />
            </button>
          </div>
        </>
      ) : (
        <span className="text-xs text-gray-400">No preview</span>
      )}
    </div>
  </div>
);

const ImageTransformTool = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [uploadedImage, setUploadedImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const [outputImages, setOutputImages] = useState([]);
  const [pythonImage, setPythonImage] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(0);
  const [isFrameGalleryOpen, setIsFrameGalleryOpen] = useState(false);
  const projectname = localStorage.getItem("projectname");
  const organizationId = localStorage.getItem("organizationId");
  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};
  const user = JSON.parse(localStorage.getItem("user"));
  const [activeTab, setActiveTab] = useState("custom");

  const [preprocessing, setPreprocessing] = useState({
    resize: { enabled: false, label: "Image Resizing", width: 512, height: 512 },
    grayscale: { enabled: false, label: "Grayscale Conversion" },
    autoEdge: { enabled: false, label: "Auto Edge Contrast" , method: "contrast_stretching",
    fixedSize: 400,
    strength: 1.5,
    threshold: 0.1}
  });

  const [augmentation, setAugmentation] = useState({
    flip: {
      enabled: false,
      label: "Flip",
      horizontal: false,
      vertical: false, direction: "horizontal"
    },
    randomCrop: { enabled: false, label: "Random Crop", percentage: 80, direction: "random" },
    rotation: { enabled: false, label: "Rotation", angle: 0},
    blur: { enabled: false, label: "Blur", intensity: 0 },
    saturation: { enabled: false, label: "Saturation", value: 1 },
    brightness: { enabled: false, label: "Brightness", value: 1 },
    exposure: { enabled: false, label: "Exposure", value: 1 },
    shear: { enabled: false, label: "Shear", horizontal: 0, vertical: 0 }
  });
  const [cameraUsecaseMap, setCameraUsecaseMap] = useState({});
  const [selectedUsecase, setSelectedUsecase] = useState([]);
  const [previewFrame, setPreviewFrame] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewBase64, setPreviewBase64] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDashboardPrompt, setShowDashboardPrompt] = useState(false);
  const outputSectionRef = useRef(null);
  const { cameraId, cameraName, models = [] } = location.state || {};
  const [savedPipelineSteps, setSavedPipelineSteps] = useState([]);

  useEffect(() => {
    if (models.length > 0) {
      setSelectedUsecase(
        models.map(m => `${m.name}__${m.modelType}`)
      );
    }
  }, [cameraId, activeTab]);

  const fetchPipeline = async () => {

    try{
      const res = await axios.post("/get-camera-pipeline", {
        projectname,
        organizationId,
        cameraId,
      });

      if (res?.data?.pipeline) {
      const {
        pipeline: savedPipeline,
        preprocessing: savedPre,
        augmentation: savedAug
      } = res.data.pipeline;

      // ✅ Summary strip
      setSavedPipelineSteps(savedPipeline || []);

      // ✅ Main pipeline (drag & drop + cards)
      setPipeline(savedPipeline || []);

      // ✅ Restore toggles
      setPreprocessing(savedPre || {});
      setAugmentation(savedAug || {});
    }
    } catch (err) {
      if (err.response && err.response.data) {
        message.warning(err.response.data.msg ? err.response.data.msg : err.response.data);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error("User not authorised");
      } else {
        message.error("Something went wrong");
      }
    }
  }

  useEffect(() => {
    // fetchPipeline();
  }, [cameraId]);

  const openImagePreview = (src) => {
    setPreviewImageSrc(src);
    setIsImagePreviewOpen(true);
  };

  const resetAllTransformations = () => {
    // Reset preprocessing
    const resetPre = {};
    Object.entries(preprocessing).forEach(([key, value]) => {
      resetPre[key] = { ...value, enabled: false };
    });

    // Reset augmentation
    const resetAug = {};
    Object.entries(augmentation).forEach(([key, value]) => {
      if (key === "flip") {
        resetAug[key] = {
          ...value,
          enabled: false,
          horizontal: false,
          vertical: false
        };
      } else {
        resetAug[key] = { ...value, enabled: false };
      }
    });

    setPreprocessing(resetPre);
    setAugmentation(resetAug);
    setPipeline([]);
    setOutputImages([]);
    setProcessedImage(null);
    setPythonImage(null);
  };

  const handleSavePipeline = async () => {
    if (!cameraId) {
      message.warning("Please select a camera");
      return;
    }

    try {
      setIsSaving(true);

      const parsedUsecases = selectedUsecase.map(val => {
        const [name, modelType] = val.split("__");
        return { name, modelType };
      });

      await axios.post("/savedataflow-pipeline", {
        username: user.username,
        projectname,
        organizationId,
        cameraId: cameraId,
        usecase: parsedUsecases,
        preprocessing,
        augmentation,
        pipeline
      });
      
      fetchPipeline()

      message.success("Dataflow pipeline saved successfully");
    } catch (err) {
      message.error("Error saving pipeline");
    } finally {
      setIsSaving(false);
    }
  };

  const updateAugmentationValue = (key, field, value) => {
    setAugmentation(prev => {
      const updated = {
        ...prev,
        [key]: { ...prev[key], [field]: value }
      };

      if (key === "flip") {
        setPipeline(prevPipeline => {
          let next = prevPipeline.filter(
            step =>
              step.type !== "flip(horizontal)" &&
              step.type !== "flip(vertical)"
          );

          if (updated.flip.horizontal) {
            next.push({
              type: "flip(horizontal)",
              config: { direction: "horizontal" }
            });
          }

          if (updated.flip.vertical) {
            next.push({
              type: "flip(vertical)",
              config: { direction: "vertical" }
            });
          }

          return next;
        });
      } else {
        upsertPipelineStep(key, updated[key], updated[key].enabled);
      }

      return updated;
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setProcessedImage(null);
        setCapturedFrames([]);
        setSelectedFrameIndex(0);
        setDataSource("upload");
      };
      reader.readAsDataURL(file);
    }
  };

  const togglePreprocessing = (key) => {
    setPreprocessing(prev => {
      const enabled = !prev[key].enabled;

      upsertPipelineStep(key, prev[key], enabled);

      return {
        ...prev,
        [key]: { ...prev[key], enabled }
      };
    });
  };

  const updatePipelineConfig = (type, field, value) => {
    setPipeline(prev =>
      prev.map(step =>
        step.type === type
          ? {
              ...step,
              config: {
                ...step.config,
                [field]: value
              }
            }
          : step
      )
    );
  };

  const toggleAugmentation = (key) => {
    setAugmentation(prev => {
      const enabled = !prev[key].enabled;

      if (key === "flip") {
        setPipeline(prevPipeline =>
          prevPipeline.filter(
            step =>
              step.type !== "flip(horizontal)" &&
              step.type !== "flip(vertical)"
          )
        );

        return {
          ...prev,
          flip: {
            ...prev.flip,
            enabled,
            horizontal: false,
            vertical: false
          }
        };
      }

      upsertPipelineStep(key, prev[key], enabled);

      return {
        ...prev,
        [key]: { ...prev[key], enabled }
      };
    });
  };

  const handleStartLiveStreamCapture = async () => {
    if (!cameraId) {
      message.warning("Please select a camera first");
      return;
    }

    try {
      setIsPreviewLoading(true);

      const res = await axios.post("/pre-trained-preview-frame", {
        projectname,
        cameraId: cameraId,
      });

      if (res?.data?.success) {
        setPreviewBase64(res.data.previewBase64);
        setIsPreviewModalOpen(true);
        message.success("Preview frame captured successfully");
      } else {
        message.error("Failed to capture preview frame");
      }

    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else {
        message.error("Unable to capture preview frame");
      }
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleFrameSelect = (index) => {
    setSelectedFrameIndex(index);
    setUploadedImage(capturedFrames[index].url);
    setProcessedImage(null);
    setOutputImages([]);
  };

  const upsertPipelineStep = (type, config, enabled) => {
    setPipeline(prev => {
      if (type === "flip") return prev;

      if (!enabled) {
        return prev.filter(step => step.type !== type);
      }

      if (prev.some(step => step.type === type)) {
        return prev.map(step =>
          step.type === type
            ? { ...step, config: { ...config } }
            : step
        );
      }

      return [...prev, { type, config: { ...config } }];
    });
  };

  const applyTransformations = async (pipeline) => {
    if (!uploadedImage) {
      setOutputImages([]);
      setProcessedImage(null);
      return;
    }

    const cleanPipeline = pipeline.map(step => ({
      type: step.type,
      config: Object.fromEntries(
        Object.entries(step.config).filter(
          ([k]) => k !== "enabled" && k !== "label"
        )
      )
    }));

    setIsProcessing(true);
    setOutputImages([]);

    try {
      const res = await axios.post("/preview-transform", {
        imageBase64: uploadedImage,
        pipeline: cleanPipeline,
        usecases: selectedUsecase,
      }, {timeout: 120000});

      if (res.data.success) {
        setOutputImages(res.data.steps);
        setProcessedImage(res.data.steps.at(-1)?.base64 || null);
        setPythonImage(res.data.pythonImage || null);
        message.success("Transformations applied successfully");

        setTimeout(() => {
          outputSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }, 200);
      } else {
        message.error("Transformation failed");
      }
    } catch (err) {
      message.error("Server processing failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const getEnabledCount = () => {
    const preCount = Object.values(preprocessing).filter(v => v.enabled).length;
    const augCount = Object.values(augmentation).filter(v => v.enabled).length;
    return preCount + augCount;
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  useEffect(() => {
    if (pipeline.length === 0) {
      setOutputImages([]);
      setProcessedImage(null);
    }
  }, [pipeline]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Reset states when switching tabs
    setUploadedImage(null);
    setProcessedImage(null);
    setOutputImages([]);
    setPipeline([]);
    setPythonImage(null);
    setCapturedFrames([]);
    setDataSource(null);
    setSelectedCamera(null);
    setSelectedUsecase([])
    resetAllTransformations();
  };

  const PipelineArrow = () => (
    <div className="flex items-center justify-center min-w-[40px] text-gray-400">
      <ChevronRight className="w-6 h-6" />
    </div>
  );

  const handleDragOver = (e) => {
  e.preventDefault();
};

const handleDrop = (e) => {
  e.preventDefault();

  // ❌ Do nothing if image already uploaded
  if (uploadedImage) return;

  const file = e.dataTransfer.files[0];
  if (file && file.type.startsWith("image/")) {
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
      setProcessedImage(null);
      setCapturedFrames([]);
      setSelectedFrameIndex(0);
      setDataSource("upload");
    };
    reader.readAsDataURL(file);
  } else {
    message.warning("Please drop a valid image file");
  }
};


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header auth={citriot?.auth} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1600px]">

        {/* Modern Header */}
        <div className="mb-8 sm:mb-10">
          <div className="mb-4">
            <button
              onClick={() => navigate('/selectmodel')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-all"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                Back to Model Selection
              </span>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <Sparkles className="text-white w-6 h-6 sm:w-7 sm:h-7" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent">
                    Pre-Deployment Image Dataflow
                  </h1>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {getEnabledCount()} transformation{getEnabledCount() !== 1 ? 's' : ''} active
                    {capturedFrames.length > 0 && ` • ${capturedFrames.length} frames captured`}
                  </p>
                </div>
              </div>
            </div>
            
            
            <div className="flex items-center gap-3">
              {capturedFrames.length > 0 && (
                <button
                  onClick={() => setIsFrameGalleryOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-all shadow-sm"
                >
                  <Grid3x3 className="w-4 h-4" />
                  <span>View All Frames</span>
                  <Badge count={capturedFrames.length} className="ml-1" />
                </button>
              )}
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">Ready</span>
              </div>
            </div>
          </div>
        </div>
        {/* ===== PIPELINE SUMMARY ===== */}
          {savedPipelineSteps.length > 0 && (
            <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-800">
                  Existing Dataflow Pipeline for Camera {cameraId}
                </h3>
                <Tag color="green">Loaded</Tag>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto">
                <Tag color="default">Original</Tag>

                {savedPipelineSteps.map((step, index) => (
                  <React.Fragment key={`${step.type}-${index}`}>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <Tag color="blue">{step.type}</Tag>
                  </React.Fragment>
                ))}

                <ChevronRight className="w-4 h-4 text-gray-400" />
                <Tag color="purple">Final</Tag>
              </div>
            </div>
          )}

        {/* Tabs for Custom and Capture Preview */}
        <div className="mb-6">
          <Tabs 
            activeKey={activeTab} 
            onChange={handleTabChange}
            size="large"
            className="custom-tabs"
          >
            <TabPane 
              tab={
                <span className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Custom Upload
                </span>
              } 
              key="custom"
            />
            <TabPane 
              tab={
                <span className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Capture Preview
                </span>
              } 
              key="capture"
            />
          </Tabs>
        </div>

        <div className="mb-6 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-2xl border-2 border-blue-200 shadow-sm overflow-hidden">
          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                <AlertCircle className="w-5 h-5 text-white" />
              </div> */}
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                <svg 
                  className="w-5 h-5 text-white" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-bold text-gray-900">
                    Important: Data Flow Configuration
                  </h4>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Select a camera and capture a preview to save the data flow configuration properly.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {activeTab === "capture" && (
          <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Camera Select */}
              {/* <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Select Camera
                </label>
                <Select
                  placeholder="Choose a camera"
                  value={selectedCamera}
                  onChange={(channel) => {
                    setSelectedCamera(channel);
                    setSelectedUsecase(cameraUsecaseMap[channel] || []);
                  }}
                  className="w-full"
                  size="large"
                >
                  {cameras.map(cam => (
                    <Select.Option key={cam.channel} value={cam.channel}>
                      {cam.cameraName} — Channel {cam.channel}
                    </Select.Option>
                  ))}
                </Select>

              </div> */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Camera</p>
              <p className="text-sm font-semibold text-gray-900">
                {cameraName ? `${cameraName} - Channel ${cameraId}` : `Channel ${cameraId}`}
              </p>
            </div>

              {/* Auto-selected Use Case */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Assigned Models
                </label>
                <Select
                  mode="multiple"
                  value={selectedUsecase}
                  onChange={setSelectedUsecase}
                  size="large"
                  className="w-full"
                  placeholder="Select models for this camera"
                >
                  {models.map((m) => (
                    <Select.Option
                      key={`${m.name}-${m.modelType}`}
                      value={`${m.name}__${m.modelType}`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{m.name}</span>
                        <Tag color={m.modelType === "pretrained" ? "blue" : "purple"}>
                          {m.modelType === "pretrained" ? "Pre-trained" : "Custom"}
                        </Tag>
                      </div>
                    </Select.Option>
                  ))}
                </Select>

              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

          {/* Left Panel - Upload & Controls */}
          <div className="xl:col-span-3 space-y-5">
            
            {/* Upload Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-2">
                  {activeTab === "custom" ? (
                    <>
                      <Upload className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-gray-900">Upload Image</h3>
                    </>
                  ) : (
                    <>
                      <Camera className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-gray-900">Camera Preview</h3>
                    </>
                  )}
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Custom Upload Tab Content */}
                {activeTab === "custom" && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={!!uploadedImage}
                      className="hidden"
                    />

                    <div
                      onClick={() => {
                        if (!uploadedImage) fileInputRef.current.click();
                      }}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      className={`w-full group relative ${
                        uploadedImage ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >

                      <div
  className={`relative py-12 border-2 border-dashed rounded-xl transition-all ${
    uploadedImage
      ? "border-gray-200 bg-gray-100 opacity-60"
      : "border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-white"
  }`}
>
  <div className="flex flex-col items-center">
    <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
      uploadedImage ? "bg-gray-200" : "bg-indigo-100"
    }`}>
      <ImageIcon className={`w-7 h-7 ${
        uploadedImage ? "text-gray-400" : "text-indigo-600"
      }`} />
    </div>

    <p className="text-sm font-medium text-gray-700 mb-1">
      {uploadedImage ? "Image uploaded" : "Drop your image here"}
    </p>

    <p className="text-xs text-gray-500">
      {uploadedImage ? "Delete image to upload another" : "or click to browse"}
    </p>
  </div>
</div>

                    </div>
                  </>
                )}

                {/* Capture Preview Tab Content */}
                {activeTab === "capture" && (
                  <button
                    onClick={handleStartLiveStreamCapture}
                    disabled={selectedUsecase.length === 0 || isPreviewLoading}
                    className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                      selectedUsecase.length === 0
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : uploadedImage && dataSource === "stream"
                        ? "bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600"
                        : "bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600"
                    }`}
                  >
                    <Camera className="w-4 h-4" />
                    <span>
                      {isPreviewLoading
                        ? "Capturing..."
                        : uploadedImage && dataSource === "stream"
                        ? "Recapture Frame"
                        : "Capture Preview"}
                    </span>
                  </button>
                )}

                {uploadedImage && (
                  <div className="space-y-3">
                    <div className="relative group rounded-xl overflow-hidden border-2 border-gray-200">
                      <img src={uploadedImage} className="w-full h-48 object-cover" alt="Uploaded" />

                      {/* 👁 Preview */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => openImagePreview(uploadedImage)}
                          className="bg-white rounded-full p-2 shadow-lg hover:scale-110 transition"
                        >
                          <Eye className="w-5 h-5 text-gray-800" />
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          setUploadedImage(null);
                          setProcessedImage(null);
                          setOutputImages([]);
                          setPipeline([]);
                          setPythonImage(null);
                          setCapturedFrames([]);
                          setDataSource(null);
                          resetAllTransformations();

                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-500 hover:text-red-600 rounded-full p-2 shadow-md transition hover:scale-110 backdrop-blur-sm"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Frame Navigation - Only show when stream frames exist */}
                    {capturedFrames.length > 0 && dataSource === "stream" && (
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 border border-emerald-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-emerald-900">
                            Frame {selectedFrameIndex + 1} of {capturedFrames.length}
                          </span>
                          <span className="text-xs text-emerald-600">
                            {new Date(capturedFrames[selectedFrameIndex].timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleFrameSelect(Math.max(0, selectedFrameIndex - 1))}
                            disabled={selectedFrameIndex === 0}
                            className="p-2 bg-white rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="w-4 h-4 text-emerald-600" />
                          </button>
                          <div className="flex-1 h-2 bg-emerald-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                              style={{ width: `${((selectedFrameIndex + 1) / capturedFrames.length) * 100}%` }}
                            />
                          </div>
                          <button
                            onClick={() => handleFrameSelect(Math.min(capturedFrames.length - 1, selectedFrameIndex + 1))}
                            disabled={selectedFrameIndex === capturedFrames.length - 1}
                            className="p-2 bg-white rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-4 h-4 text-emerald-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Apply Button */}
                <button
                  onClick={()=>applyTransformations(pipeline)}
                  disabled={isProcessing || !uploadedImage || selectedUsecase.length === 0}
                  className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-md ${
                    isProcessing || !uploadedImage || selectedUsecase.length === 0
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg"
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      <span>Apply Transformations</span>
                    </>
                  )}
                </button>
                {activeTab === "capture" && (
                  <button
                    onClick={handleSavePipeline}
                    disabled={
                      isSaving ||                 // already saving
                      selectedUsecase.length === 0 ||
                      !pythonImage
                    }
                    className={`w-full mt-3 py-3 rounded-xl font-semibold transition-all shadow-md ${
                      selectedUsecase.length === 0 ||
                      isSaving || !pythonImage
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg"
                    }`}
                  >
                    {isSaving ? "Saving..." : "Save Dataflow Configuration"}
                  </button>
                )}
              </div>
            </div>
            {activeTab === "custom" && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Models
                </label>

                <Select
                  mode="multiple"
                  value={selectedUsecase}
                  onChange={setSelectedUsecase}
                  size="large"
                  className="w-full"
                  placeholder="Select models for this camera"
                >
                  {models.map((m) => (
                    <Select.Option
                      key={`${m.name}-${m.modelType}`}
                      value={`${m.name}__${m.modelType}`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{m.name}</span>
                        <Tag color={m.modelType === "pretrained" ? "blue" : "purple"}>
                          {m.modelType === "pretrained" ? "Pre-trained" : "Custom"}
                        </Tag>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}


            {/* Stats Card */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl p-5 text-white shadow-lg">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm text-indigo-100 mb-1">Active Filters</p>
                  <p className="text-3xl font-bold">{getEnabledCount()}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6" />
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex-1 bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-white rounded-full h-2 transition-all"
                    style={{ width: `${Math.min(100, (getEnabledCount() / 10) * 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-indigo-100">of 10</span>
              </div>
            </div>
          </div>

          {/* Middle Panel - Preprocessing */}
          <div className="xl:col-span-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden h-full">
              
              {/* Header */}
              <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Preprocessing</h3>
                  <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg">
                    {Object.values(preprocessing).filter(v => v.enabled).length} active
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-3">
                {Object.entries(preprocessing).map(([key, value]) => (
                  <div
                    key={key}
                    className={`w-full rounded-xl p-4 transition-all ${
                      value.enabled
                        ? "bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 shadow-sm"
                        : "bg-gray-50 border-2 border-gray-200 hover:border-amber-300 hover:bg-amber-50/50"
                    }`}
                  >
                    {/* 🔘 Toggle Header */}
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => togglePreprocessing(key)}
                    >
                      <div>
                        <p className={`font-medium ${value.enabled ? "text-gray-900" : "text-gray-700"}`}>
                          {value.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {value.enabled ? "Enabled" : "Click to enable"}
                        </p>
                      </div>

                      {value.enabled && (
                        <CheckCircle className="w-5 h-5 text-amber-600" />
                      )}
                    </div>

                    {/* 🔧 Resize Controls */}
                    {key === "resize" && value.enabled && (
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Width</label>
                          <InputNumber
                            min={32}
                            max={2048}
                            value={value.width}
                            onChange={(v) => {
                              setPreprocessing(prev => ({
                                ...prev,
                                resize: { ...prev.resize, width: v }
                              }));
                              updatePipelineConfig("resize", "width", v);
                            }}
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">Height</label>
                          <InputNumber
                            min={32}
                            max={2048}
                            value={value.height}
                            onChange={(v) => {
                              setPreprocessing(prev => ({
                                ...prev,
                                resize: { ...prev.resize, height: v }
                              }));
                              updatePipelineConfig("resize", "height", v);
                            }}
                            className="w-full"
                          />
                        </div>
                      </div>
                    )}

                    {/* 🔧 Auto Edge Controls */}
                    {key === "autoEdge" && value.enabled && (
                      <div className="mt-4 space-y-3">
                        <label className="text-sm font-medium text-gray-700">
                          Edge Enhancement Method
                        </label>

                        <Select
                          value={value.method}
                          onChange={(method) => {
                            let config = { method };

                            if (method === "contrast_stretching") {
                              config = { ...config, fixedSize: 400, strength: 1.5, threshold: 0.1 };
                            }
                            if (method === "histogram_equalization") {
                              config = { ...config, fixedSize: 400, strength: 1.3, threshold: 0.15 };
                            }
                            if (method === "adaptive_equalization") {
                              config = { ...config, fixedSize: 400, strength: 1.7, threshold: 0.2 };
                            }

                            setPreprocessing(prev => ({
                              ...prev,
                              autoEdge: { ...prev.autoEdge, ...config }
                            }));

                            Object.entries(config).forEach(([k, v]) =>
                              updatePipelineConfig("autoEdge", k, v)
                            );
                          }}
                          className="w-full"
                        >
                          <Select.Option value="contrast_stretching">
                            Contrast Stretching
                          </Select.Option>
                          <Select.Option value="histogram_equalization">
                            Histogram Equalization
                          </Select.Option>
                          <Select.Option value="adaptive_equalization">
                            Adaptive Equalization (CLAHE)
                          </Select.Option>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Augmentation */}
          <div className="xl:col-span-5">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden h-full flex flex-col">
              <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">Augmentation</h3>
                  </div>
                  <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg">
                    {Object.values(augmentation).filter(v => v.enabled).length} active
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-3 overflow-y-auto flex-1" style={{ maxHeight: "calc(100vh - 20px)" }}>
                {Object.entries(augmentation).map(([key, value]) => (
                  <div
                    key={key}
                    className={`group transition-all ${
                      value.enabled 
                        ? "bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-400 shadow-sm" 
                        : "bg-gray-50 border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50"
                    } rounded-xl p-4`}
                  >
                    <div
                      onClick={() => toggleAugmentation(key)}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-left">
                          <p className={`font-medium ${value.enabled ? "text-gray-900" : "text-gray-700"}`}>
                            {value.label}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {value.enabled ? "Enabled" : "Click to enable"}
                          </p>
                        </div>
                      </div>
                      {value.enabled && (
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      )}
                    </div>

                    {/* Controls */}
                    {value.enabled && (
                      <div className="mt-4 pt-4 border-t border-emerald-200/50">
                        {/* FLIP */}
                        {key === "flip" && (
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                updateAugmentationValue("flip", "horizontal", !value.horizontal)
                              }
                              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                value.horizontal
                                  ? "bg-emerald-500 text-white shadow-sm"
                                  : "bg-white text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              Horizontal
                            </button>

                            <button
                              onClick={() =>
                                updateAugmentationValue("flip", "vertical", !value.vertical)
                              }
                              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                value.vertical
                                  ? "bg-emerald-500 text-white shadow-sm"
                                  : "bg-white text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              Vertical
                            </button>
                          </div>
                        )}

                        {/* ROTATION */}
                        {key === "rotation" && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">Angle</label>
                              <span className="text-sm font-semibold text-emerald-600">{value.angle}°</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="360"
                              value={value.angle}
                              onChange={e =>
                                updateAugmentationValue("rotation", "angle", +e.target.value)
                              }
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                            />
                          </div>
                        )}

                        {/* RANDOM CROP */}
                        {key === "randomCrop" && (
  <div className="space-y-3">
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-medium text-gray-700">Crop Size</label>
      <span className="text-sm font-semibold text-emerald-600">
        {value.percentage}%
      </span>
    </div>

    <input
      type="range"
      min="10"
      max="100"
      value={value.percentage}
      onChange={e =>
        updateAugmentationValue("randomCrop", "percentage", +e.target.value)
      }
      className="w-full"
    />

    <Select
      value={value.direction}
      onChange={dir =>
        updateAugmentationValue("randomCrop", "direction", dir)
      }
      className="w-full"
    >
      <Select.Option value="random">Random</Select.Option>
      <Select.Option value="left">Left</Select.Option>
      <Select.Option value="right">Right</Select.Option>
      <Select.Option value="top">Top</Select.Option>
      <Select.Option value="bottom">Bottom</Select.Option>
      <Select.Option value="center">Center</Select.Option>
    </Select>
  </div>
)}


                        {/* BLUR */}
                        {key === "blur" && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">Intensity</label>
                              <span className="text-sm font-semibold text-emerald-600">{value.intensity}px</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="10"
                              value={value.intensity}
                              onChange={e =>
                                updateAugmentationValue("blur", "intensity", +e.target.value)
                              }
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                            />
                          </div>
                        )}

                        {/* BRIGHTNESS / SATURATION / EXPOSURE */}
                        {["brightness", "saturation", "exposure"].includes(key) && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">Level</label>
                              <span className="text-sm font-semibold text-emerald-600">{value.value.toFixed(1)}x</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="3"
                              step="0.1"
                              value={value.value}
                              onChange={e =>
                                updateAugmentationValue(key, "value", +e.target.value)
                              }
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                            />
                          </div>
                        )}

                        {key === "shear" && (
                          <div className="space-y-3">

                            <div>
                              <div className="flex justify-between">
                                <label className="text-sm">Horizontal</label>
                                <span>{value.horizontal}°</span>
                              </div>

                              <input
                                type="range"
                                min="0"
                                max="45"
                                value={value.horizontal}
                                onChange={(e)=>
                                  updateAugmentationValue("shear","horizontal",+e.target.value)
                                }
                                className="w-full"
                              />
                            </div>

                            <div>
                              <div className="flex justify-between">
                                <label className="text-sm">Vertical</label>
                                <span>{value.vertical}°</span>
                              </div>

                              <input
                                type="range"
                                min="0"
                                max="45"
                                value={value.vertical}
                                onChange={(e)=>
                                  updateAugmentationValue("shear","vertical",+e.target.value)
                                }
                                className="w-full"
                              />
                            </div>

                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Output Gallery */}
        {(outputImages.length > 0 || pythonImage) && (
          <div ref={outputSectionRef} className="mt-8 grid grid-cols-1 xl:grid-cols-5 gap-2">
            {/* Pipeline Card */}
            <div className="xl:col-span-4 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-purple-50">
                <h2 className="text-xl font-bold text-gray-900">
                  Transformation Pipeline
                </h2>
                <p className="text-sm text-gray-600">
                  {outputImages.length} steps completed
                </p>
              </div>

              <div className="p-4">
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) return;

                    const newPipeline = reorder(
                      pipeline,
                      result.source.index,
                      result.destination.index
                    );

                    setPipeline(newPipeline);
                    if (uploadedImage) {
                      applyTransformations(newPipeline);
                    }
                  }}
                >
                  <Droppable droppableId="pipeline" direction="horizontal">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex items-center gap-3 overflow-x-auto pb-4"
                      >
                        <PipelineCard
                          label="Original"
                          image={uploadedImage}
                          locked
                          onPreview={openImagePreview}
                        />

                        { pipeline.length > 0 && <><PipelineArrow />

                        {pipeline.map((step, index) => {
                          const previews = outputImages.filter(p => p.label === step.type);
                          if (!previews) return null;

                          return (
                            <React.Fragment key={`${step.type}-${index}`}>
                              <Draggable
                                draggableId={`${step.type}-${index}`}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <PipelineCard
                                      label={step.type}
                                      image={previews[0]?.base64}
                                      images={previews.map(p => p.base64)}
                                      onPreview={openImagePreview}
                                    />
                                  </div>
                                )}
                              </Draggable>

                              {index !== pipeline.length - 1 && (
                                <PipelineArrow />
                              )}
                            </React.Fragment>
                          );
                        })}

                         <PipelineArrow />
                        <PipelineCard
                          label="Final"
                          image={processedImage}
                          locked
                          onPreview={openImagePreview} /></>}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>

            {/* Model Output Card */}
            {pythonImage && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
                  <h2 className="text-xl font-bold text-gray-900">
                    Model Output
                  </h2>
                  <p className="text-sm text-gray-600">
                    Generated by model inference
                  </p>
                </div>

                <div className="p-6 flex justify-center">
                  <PipelineCard
                    label="Output"
                    image={pythonImage}
                    locked
                    onPreview={openImagePreview}
                  />
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Frame Gallery Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
              <Grid3x3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Captured Frames</h3>
              <p className="text-xs text-gray-500 font-normal">{capturedFrames.length} frames available</p>
            </div>
          </div>
        }
        open={isFrameGalleryOpen}
        onCancel={() => setIsFrameGalleryOpen(false)}
        footer={null}
        centered
        width={1000}
      >
        <div className="pt-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 max-h-[600px] overflow-y-auto">
            {capturedFrames.map((frame, index) => (
              <div
                key={frame._id}
                onClick={() => {
                  handleFrameSelect(index);
                  setIsFrameGalleryOpen(false);
                }}
                className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all hover:shadow-lg ${
                  selectedFrameIndex === index
                    ? "border-emerald-500 shadow-md"
                    : "border-gray-200 hover:border-emerald-300"
                }`}
              >
                <img
                  src={frame.url}
                  alt={`Frame ${index + 1}`}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-semibold">Frame {index + 1}</p>
                    <p className="text-white/80 text-[10px]">
                      {new Date(frame.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                {selectedFrameIndex === index && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={isImagePreviewOpen}
        footer={null}
        centered
        width={900}
        onCancel={() => setIsImagePreviewOpen(false)}
        closeIcon={
          <CloseOutlined style={{ fontSize: 18 }} />
        }
        styles={{
          header: { paddingTop: 10 },
        }}
        className="custom-preview-modal"
      >
        <div className="flex justify-center items-center">
          <img
            src={previewImageSrc}
            alt="Preview"
            className="max-h-[80vh] max-w-full object-contain rounded-xl"
          />
        </div>
      </Modal>

      {/* Camera Preview Modal */}
      <Modal
        open={isPreviewModalOpen}
        footer={null}
        centered
        width={600}
        onCancel={() => setIsPreviewModalOpen(false)}
      >
        <div className="space-y-4">
          <h3 className="text-lg font-bold">Camera Preview</h3>

          <img
            src={previewBase64}
            alt="Preview"
            className="w-full rounded-xl border"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={() => {
                setIsPreviewModalOpen(false);
                setPreviewFrame(null);
                handleStartLiveStreamCapture()
              }}
            >
              Recapture
            </Button>

            <Button
              type="primary"
              onClick={() => {
                setUploadedImage(previewBase64);
                setDataSource("stream");
                setIsPreviewModalOpen(false);
              }}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 border-0"
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={showDashboardPrompt}
        centered
        footer={null}
        closable={false}
      >
        <div className="text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
            <LayoutDashboard size={28} className="text-blue-600" />
          </div>

          <h3 className="text-lg font-bold">
            Dashboard Not Configured
          </h3>

          <p className="text-sm text-gray-600">
            You’ve completed dataflow setup.
            Would you like to configure your dashboard now?
          </p>

          <div className="flex justify-center gap-3 pt-4">
            <Button
              onClick={() => setShowDashboardPrompt(false)}
            >
              Later
            </Button>

            <Button
              type="primary"
              onClick={() => {
                setShowDashboardPrompt(false);
                navigate("/sidebar/Dashboard-Setting");
              }}
            >
              Configure Dashboard
            </Button>
          </div>
        </div>
      </Modal>


    </div>
  );
};

export default ImageTransformTool;